import React, { useState, useEffect, useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import {
  MapPin,
  TrendingUp,
  Clock,
  Target,
  Activity,
  AlertTriangle,
  CheckCircle,
  Ruler,
  Download,
  RefreshCw
} from 'lucide-react';

// Import your actual types and services
import { Work, WorkStage } from '../../types/work.types';
import { worksApiService } from '../../services/workApi.service';
import { masterDataAPI } from '../../services/master-data-api.service';

interface PhysicalProgressOverview {
  totalSanctionedKm: number;
  totalCompletedKm: number;
  balanceWorkKm: number;
  overallProgress: number;
  onTrackWorks: number;
  delayedWorks: number;
  completedWorks: number;
  totalWorks: number;
}

interface DistrictPhysicalProgress {
  district_id: number;
  district_name: string;
  sanctioned_km: number;
  completed_km: number;
  balance_km: number;
  progress_percentage: number;
  status: 'On Track' | 'Delayed' | 'Critical';
  mandals_count: number;
  works_count: number;
  completed_works: number;
  delayed_works: number;
}

interface MandalPhysicalProgress {
  mandal_id: number;
  mandal_name: string;
  district_name: string;
  sanctioned_km: number;
  completed_km: number;
  balance_km: number;
  progress_percentage: number;
  works_count: number;
}

const PhysicalProgressDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<number | null>(null);
  const [viewType, setViewType] = useState<'district' | 'mandal'>('district');

  // State for dashboard data
  const [overview, setOverview] = useState<PhysicalProgressOverview | null>(null);
  const [districtProgress, setDistrictProgress] = useState<DistrictPhysicalProgress[]>([]);
  const [mandalProgress, setMandalProgress] = useState<MandalPhysicalProgress[]>([]);
  const [allWorks, setAllWorks] = useState<Work[]>([]);

  // Calculate overview metrics from works data
  const calculateOverviewMetrics = (works: Work[]): PhysicalProgressOverview => {
    // Ensure we have valid works array
    if (!Array.isArray(works) || works.length === 0) {
      return {
        totalSanctionedKm: 0,
        totalCompletedKm: 0,
        balanceWorkKm: 0,
        overallProgress: 0,
        onTrackWorks: 0,
        delayedWorks: 0,
        completedWorks: 0,
        totalWorks: 0
      };
    }

    const totalSanctionedKm = works.reduce((sum, work) => {
      const value = Number(work.length_sanctioned_km) || 0;
      return sum + value;
    }, 0);
    
    const totalCompletedKm = works.reduce((sum, work) => {
      const value = Number(work.cumulative_length_completed) || 0;
      return sum + value;
    }, 0);
    
    const balanceWorkKm = totalSanctionedKm - totalCompletedKm;
    const overallProgress = totalSanctionedKm > 0 ? (totalCompletedKm / totalSanctionedKm) * 100 : 0;
    
    const completedWorks = works.filter(w => w && w.abstract_stage_of_work === WorkStage.COMPLETED).length;
    const delayedWorks = works.filter(w => w && worksApiService.isWorkDelayed(w)).length;
    const onTrackWorks = works.filter(w => 
      w && w.abstract_stage_of_work === WorkStage.IN_PROGRESS && 
      !worksApiService.isWorkDelayed(w)
    ).length;

    return {
      totalSanctionedKm: Number(totalSanctionedKm) || 0,
      totalCompletedKm: Number(totalCompletedKm) || 0,
      balanceWorkKm: Number(balanceWorkKm) || 0,
      overallProgress: Number(overallProgress) || 0,
      onTrackWorks: Number(onTrackWorks) || 0,
      delayedWorks: Number(delayedWorks) || 0,
      completedWorks: Number(completedWorks) || 0,
      totalWorks: works.length || 0
    };
  };

  // Calculate district-wise progress
  const calculateDistrictProgress = (works: Work[]): DistrictPhysicalProgress[] => {
    // Safety check for works array
    if (!Array.isArray(works) || works.length === 0) {
      return [];
    }

    const districtMap = new Map<number, {
      district_id: number;
      district_name: string;
      works: Work[];
    }>();

    // Group works by district
    works.forEach(work => {
      if (work && work.district_id) {
        const districtName = work.district?.name || 'Unknown District';
        
        if (!districtMap.has(work.district_id)) {
          districtMap.set(work.district_id, {
            district_id: work.district_id,
            district_name: districtName,
            works: []
          });
        }
        const district = districtMap.get(work.district_id);
        if (district) {
          district.works.push(work);
        }
      }
    });

    // Calculate metrics for each district
    const districtProgressData: DistrictPhysicalProgress[] = [];

    districtMap.forEach((districtData, districtId) => {
      const sanctioned_km = districtData.works.reduce((sum, work) => {
        const value = Number(work.length_sanctioned_km) || 0;
        return sum + value;
      }, 0);
      
      const completed_km = districtData.works.reduce((sum, work) => {
        const value = Number(work.cumulative_length_completed) || 0;
        return sum + value;
      }, 0);
      
      const balance_km = sanctioned_km - completed_km;
      const progress_percentage = sanctioned_km > 0 ? (completed_km / sanctioned_km) * 100 : 0;
      
      const completed_works = districtData.works.filter(w => w && w.abstract_stage_of_work === WorkStage.COMPLETED).length;
      const delayed_works = districtData.works.filter(w => w && worksApiService.isWorkDelayed(w)).length;
      
      // Determine status
      let status: 'On Track' | 'Delayed' | 'Critical' = 'On Track';
      const delayedPercentage = districtData.works.length > 0 ? (delayed_works / districtData.works.length) * 100 : 0;
      if (delayedPercentage > 30) status = 'Critical';
      else if (delayedPercentage > 10) status = 'Delayed';

      districtProgressData.push({
        district_id: districtId,
        district_name: districtData.district_name,
        sanctioned_km: Number(sanctioned_km) || 0,
        completed_km: Number(completed_km) || 0,
        balance_km: Number(balance_km) || 0,
        progress_percentage: Number(progress_percentage) || 0,
        status,
        mandals_count: 0,
        works_count: districtData.works.length || 0,
        completed_works: Number(completed_works) || 0,
        delayed_works: Number(delayed_works) || 0
      });
    });

    return districtProgressData.sort((a, b) => a.district_name.localeCompare(b.district_name));
  };

  // Calculate mandal-wise progress for selected district
  const calculateMandalProgress = async (districtId: number, works: Work[]): Promise<MandalPhysicalProgress[]> => {
    // Safety checks
    if (!districtId || !Array.isArray(works) || works.length === 0) {
      return [];
    }

    const districtWorks = works.filter(work => work && work.district_id === districtId);
    
    if (districtWorks.length === 0) {
      return [];
    }

    const mandalMap = new Map<number, {
      mandal_id: number;
      mandal_name: string;
      district_name: string;
      works: Work[];
    }>();

    // Group works by mandal
    districtWorks.forEach(work => {
      if (work && work.mandal_id) {
        const mandalName = work.mandal?.name || `Mandal ${work.mandal_id}`;
        const districtName = work.district?.name || 'Unknown District';
        
        if (!mandalMap.has(work.mandal_id)) {
          mandalMap.set(work.mandal_id, {
            mandal_id: work.mandal_id,
            mandal_name: mandalName,
            district_name: districtName,
            works: []
          });
        }
        const mandal = mandalMap.get(work.mandal_id);
        if (mandal) {
          mandal.works.push(work);
        }
      }
    });

    // Calculate metrics for each mandal
    const mandalProgressData: MandalPhysicalProgress[] = [];
    mandalMap.forEach((mandalData) => {
      const sanctioned_km = mandalData.works.reduce((sum, work) => {
        const value = Number(work.length_sanctioned_km) || 0;
        return sum + value;
      }, 0);
      
      const completed_km = mandalData.works.reduce((sum, work) => {
        const value = Number(work.cumulative_length_completed) || 0;
        return sum + value;
      }, 0);
      
      const balance_km = sanctioned_km - completed_km;
      const progress_percentage = sanctioned_km > 0 ? (completed_km / sanctioned_km) * 100 : 0;

      mandalProgressData.push({
        mandal_id: mandalData.mandal_id,
        mandal_name: mandalData.mandal_name,
        district_name: mandalData.mandal_name,
        sanctioned_km: Number(sanctioned_km) || 0,
        completed_km: Number(completed_km) || 0,
        balance_km: Number(balance_km) || 0,
        progress_percentage: Number(progress_percentage) || 0,
        works_count: mandalData.works.length || 0
      });
    });

    return mandalProgressData.sort((a, b) => a.mandal_name.localeCompare(b.mandal_name));
  };

  // Fetch all required data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch all works
        const worksResponse = await worksApiService.getAllWorks({
          limit: 10000
        });

        setAllWorks(worksResponse.works);

        // Calculate overview metrics
        const overviewData = calculateOverviewMetrics(worksResponse.works);
        setOverview(overviewData);

        // Calculate district-wise progress
        const districtProgressData = calculateDistrictProgress(worksResponse.works);
        setDistrictProgress(districtProgressData);

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Fetch mandal data when district is selected
  useEffect(() => {
    const fetchMandalData = async () => {
      if (!selectedDistrict) {
        setMandalProgress([]);
        return;
      }

      try {
        const mandalData = await calculateMandalProgress(selectedDistrict, allWorks);
        setMandalProgress(mandalData);
      } catch (err) {
        console.error('Failed to fetch mandal data:', err);
      }
    };

    fetchMandalData();
  }, [selectedDistrict, allWorks]);

  // Export data function
  const handleExportData = async () => {
    try {
      await worksApiService.downloadExcelExport({});
    } catch (error) {
      setError('Failed to export data');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading physical progress dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-center">
          <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Physical Progress Dashboard</h1>
          <p className="text-gray-600 mt-1">Track construction progress and KM-wise completion</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleExportData}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Sanctioned</p>
                <p className="text-2xl font-bold text-gray-900">
                  {(overview.totalSanctionedKm || 0).toFixed(1)} KM
                </p>
                <p className="text-sm text-gray-500 mt-1">Across {overview.totalWorks || 0} works</p>
              </div>
              <Ruler className="w-8 h-8 text-blue-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-bold text-green-600">
                  {(overview.totalCompletedKm || 0).toFixed(1)} KM
                </p>
                <p className="text-sm text-green-500 mt-1">
                  {(overview.overallProgress || 0).toFixed(1)}% Complete
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-green-500 h-2 rounded-full"
                  style={{ width: `${Math.min(overview.overallProgress || 0, 100)}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Balance Work</p>
                <p className="text-2xl font-bold text-orange-600">
                  {(overview.balanceWorkKm || 0).toFixed(1)} KM
                </p>
                <p className="text-sm text-orange-500 mt-1">
                  {(100 - (overview.overallProgress || 0)).toFixed(1)}% Remaining
                </p>
              </div>
              <Clock className="w-8 h-8 text-orange-500" />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Work Status</p>
                <div className="flex items-center space-x-4 mt-2">
                  <div className="text-center">
                    <p className="text-lg font-bold text-green-600">{overview.onTrackWorks || 0}</p>
                    <p className="text-xs text-gray-500">On Track</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-red-600">{overview.delayedWorks || 0}</p>
                    <p className="text-xs text-gray-500">Delayed</p>
                  </div>
                </div>
              </div>
              <Target className="w-8 h-8 text-purple-500" />
            </div>
          </div>
        </div>
      )}

      {/* District/Mandal Selection */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-semibold text-gray-900">Progress by Location</h2>
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => {
                  setViewType('district');
                  setSelectedDistrict(null);
                }}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  viewType === 'district'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Districts
              </button>
              <button
                onClick={() => setViewType('mandal')}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                  viewType === 'mandal'
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Mandals
              </button>
            </div>
          </div>
          
          {viewType === 'mandal' && (
            <select
              value={selectedDistrict || ''}
              onChange={(e) => setSelectedDistrict(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select District</option>
              {districtProgress.map(district => (
                <option key={district.district_id} value={district.district_id}>
                  {district.district_name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* District Progress Chart */}
        {viewType === 'district' && (
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={districtProgress} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="district_name" 
                  angle={-45}
                  textAnchor="end"
                  height={100}
                />
                <YAxis label={{ value: 'Length (KM)', angle: -90, position: 'insideLeft' }} />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    `${value.toFixed(1)} KM`,
                    name === 'sanctioned_km' ? 'Sanctioned' :
                    name === 'completed_km' ? 'Completed' : 'Balance'
                  ]}
                />
                <Legend />
                <Bar dataKey="sanctioned_km" fill="#6B7280" name="Sanctioned" />
                <Bar dataKey="completed_km" fill="#10B981" name="Completed" />
                <Bar dataKey="balance_km" fill="#F59E0B" name="Balance" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Mandal Progress Chart */}
        {viewType === 'mandal' && selectedDistrict && mandalProgress.length > 0 && (
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={mandalProgress} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="mandal_name" 
                  angle={-45}
                  textAnchor="end"
                  height={100}
                />
                <YAxis label={{ value: 'Length (KM)', angle: -90, position: 'insideLeft' }} />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    `${value.toFixed(1)} KM`,
                    name === 'sanctioned_km' ? 'Sanctioned' :
                    name === 'completed_km' ? 'Completed' : 'Balance'
                  ]}
                />
                <Legend />
                <Bar dataKey="sanctioned_km" fill="#6B7280" name="Sanctioned" />
                <Bar dataKey="completed_km" fill="#10B981" name="Completed" />
                <Bar dataKey="balance_km" fill="#F59E0B" name="Balance" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* No mandal data message */}
        {viewType === 'mandal' && !selectedDistrict && (
          <div className="text-center py-12 text-gray-500">
            <MapPin className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Select a district to view mandal-wise progress</p>
          </div>
        )}
      </div>

      {/* Detailed Progress Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {viewType === 'district' ? 'District-wise' : 'Mandal-wise'} Detailed Progress
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {viewType === 'district' ? 'District' : 'Mandal'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Sanctioned (KM)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Completed (KM)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Balance (KM)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progress %
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Works Count
                </th>
                {viewType === 'district' && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(viewType === 'district' ? districtProgress : mandalProgress).map((item, index) => (
                <tr key={`${viewType}-${index}`} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm font-medium text-gray-900">
                        {viewType === 'district' 
                          ? (item as DistrictPhysicalProgress).district_name 
                          : (item as MandalPhysicalProgress).mandal_name
                        }
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {(item.sanctioned_km || 0).toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                    {(item.completed_km || 0).toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-orange-600">
                    {(item.balance_km || 0).toFixed(1)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">
                        {(item.progress_percentage || 0).toFixed(1)}%
                      </span>
                      <div className="ml-3 w-16 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            (item.progress_percentage || 0) >= 75 
                              ? 'bg-green-500' 
                              : (item.progress_percentage || 0) >= 50 
                              ? 'bg-yellow-500' 
                              : 'bg-red-500'
                          }`}
                          style={{ width: `${Math.min(item.progress_percentage || 0, 100)}%` }}
                        ></div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.works_count || 0}
                  </td>
                  {viewType === 'district' && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        (item as DistrictPhysicalProgress).status === 'On Track' 
                          ? 'bg-green-100 text-green-800'
                          : (item as DistrictPhysicalProgress).status === 'Delayed'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {(item as DistrictPhysicalProgress).status}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PhysicalProgressDashboard;